import type { InfiniteData, InfiniteQueryObserverOptions, QueryClient } from '@tanstack/query-core';
import type { LeaderboardResponse, LeaderboardRow } from '../../shared/api';
import { uniqueBy } from '../../shared/collections';
import type { LeaderboardBoard } from '../../shared/modes';
import type { QueryHttpError } from '../api/http';
import { readLeaderboardPage } from '../api/results';

export const LIST_STALE_TIME = 30_000;
export const LIST_GC_TIME = 5 * 60_000;

export function leaderboardQueryKey(board: LeaderboardBoard, today: string, profileGeneration = 0) {
  return ['leaderboard', board, board === 'today' ? today : null, profileGeneration] as const;
}

export function leaderboardQueryOptions(
  board: LeaderboardBoard,
  today: string,
  credential: string | null = null,
  profileGeneration = 0,
): InfiniteQueryObserverOptions<
  LeaderboardResponse,
  QueryHttpError,
  InfiniteData<LeaderboardResponse>,
  readonly ['leaderboard', LeaderboardBoard, string | null, number],
  number
> {
  return {
    queryKey: leaderboardQueryKey(board, today, profileGeneration),
    queryFn: ({ pageParam, signal }) => readLeaderboardPage(board, { offset: pageParam, signal }, credential),
    initialPageParam: 0,
    getNextPageParam: (page) => page.nextOffset ?? undefined,
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
  };
}

export function leaderboardRows(data: InfiniteData<LeaderboardResponse> | undefined): LeaderboardRow[] {
  return uniqueBy(
    (data?.pages ?? []).flatMap((page) => page.rows),
    (row) => row.profileId,
  );
}

/** Refetch page one atomically so failure leaves all currently visible pages cached. */
export async function refreshLeaderboard(
  client: QueryClient,
  board: LeaderboardBoard,
  today: string,
  credential: string | null = null,
  profileGeneration = 0,
): Promise<void> {
  await client.infiniteQuery({
    ...leaderboardQueryOptions(board, today, credential, profileGeneration),
    pages: 1,
    staleTime: 0,
  });
}
